// Analytics selection axis — resolved independently of OBSERVABILITY_PROVIDER
// so the analytics sink (PostHog) composes alongside, rather than replacing,
// the logs/metrics/traces bundle. Platform-neutral: holds no SDK import, so it
// is safe to share between the web/node and the `.native` selection modules.

export type AnalyticsSelection = 'noop' | 'posthog';

export interface PostHogSessionReplayConfig {
  maskAllTextInputs: boolean;
  maskAllImages: boolean;
  maskAllSandboxedViews: boolean;
  captureLog: boolean;
  captureNetworkTelemetry: boolean;
  sampleRate?: number;
}

/** Public-by-design project key + ingestion host for a PostHog concrete. */
export interface PostHogConfig {
  /** PostHog project key. When absent, the concrete stays inert (no SDK init). */
  key?: string;
  host?: string;
  /** Allow explicitly enabled non-production evidence runs to emit synthetic browser traffic. */
  captureSyntheticTraffic?: boolean;
  /** Enable SDK interaction/page/lifecycle autocapture. Defaults to true. */
  autocapture?: boolean;
  /** Enable session replay independently from autocapture. Defaults to false. */
  sessionReplay?: boolean;
  /**
   * Enable SDK exception autocapture (unhandled errors, unhandled rejections and
   * console errors) so client-side exceptions reach PostHog with a stack. Defaults
   * to false; `$exception` payloads are credential-scrubbed rather than run
   * through the chart-property sanitizer, whose structural limits would drop the
   * stack.
   */
  captureExceptions?: boolean;
  /** Runtime environment used to enforce the production replay activation floor. */
  environment?: string;
  /** Explicit local replay sampling override. Required when replay is enabled in production. */
  sessionReplaySampleRate?: number;
  /** Client-owned replay floor. Remote configuration cannot weaken these values. */
  sessionReplayConfig?: PostHogSessionReplayConfig;
  /**
   * Paths whose very visit is the sensitive fact (ADR-079 Rule 14), so nothing
   * about them may reach PostHog: an event captured while one is current is
   * dropped, a replay batch that mentions one is dropped, and any property that
   * carries one on a later event is removed. Absent or empty changes nothing.
   */
  suppressedPathPatterns?: readonly RegExp[];
  /**
   * Pages whose heatmap points must never be sent, matched against each
   * heatmap entry's page path. Used where a click's position alone can reveal
   * something sensitive on the page (an element that only exists for some
   * subjects), so the page is excluded for every subject, uniformly.
   */
  heatmapExcludedPathPatterns?: readonly RegExp[];
  /**
   * Pages whose heatmap points on FIXED-position elements are never sent. A
   * modal or pinned panel sits at the same coordinates for every viewer, so a
   * click on it can name the option chosen; scrolling content stays measured.
   */
  heatmapFixedPointExcludedPathPatterns?: readonly RegExp[];
  /**
   * Visits to a target page that send nothing at all when they start from one
   * of the `after` pages, or when the history entry says so. For a page whose
   * visit is ordinary on its own but sensitive by what led to it.
   */
  entrySuppressedVisits?: EntrySuppressedVisits;
  /**
   * Pages that must never appear in entry attribution — `$session_entry_*`
   * and `$initial_*` — on any event. The SDK persists that attribution across
   * reloads, past anything the page itself remembers, so a visit suppressed
   * earlier could otherwise be named again later. Applied uniformly, for every
   * visit to such a page, so it discloses nothing about any one of them.
   */
  entryAttributionExcludedPathPatterns?: readonly RegExp[];
  /**
   * Pages on which autocapture-family events (`$autocapture`, `$rageclick`,
   * `$dead_click`) are never sent, judged by each event's own URL. The SDK
   * records every ancestor of a clicked element with its classes and sibling
   * position, so on a page where anything varies with a sensitive fact, a
   * click anywhere can encode it. Applied to every visit, so it discloses
   * nothing about any one; views and declared actions are unaffected.
   */
  autocaptureExcludedPathPatterns?: readonly RegExp[];
  /**
   * Pages whose scroll and content-height figures are removed from the
   * previous-pageview record (`$prev_pageview_{last,max}_{scroll,content}*`)
   * of every event that describes them. Page height can reveal an element
   * that only exists for some subjects; time on page is kept.
   */
  pageGeometryExcludedPathPatterns?: readonly RegExp[];
  /**
   * On events from the `on` pages, the previous-pageview record is removed
   * whole when it names one of the `naming` pages: where the page viewed just
   * before is itself sensitive next to this one.
   */
  previousPageviewExcluded?: {
    onPathPatterns: readonly RegExp[];
    namingPathPatterns: readonly RegExp[];
  };
  /**
   * Pages on which session replay is paused, for every subject, whenever one is
   * the current page. Batches recorded while paused are never sent, whenever
   * they are delivered. Pausing never enables replay that was not enabled.
   */
  replayPausedPathPatterns?: readonly RegExp[];
  /**
   * Also pause session replay while any `holdSessionReplay()` hold is open, on
   * every page, under the same recording-time rule as `replayPausedPathPatterns`.
   * For a surface that can appear on any page and whose presence is sensitive
   * (a panel that may hold an escalation item): the caller holds for as long as
   * the surface can be on screen, for every subject, whatever it shows.
   */
  replayPausedWhileHeld?: boolean;
  /**
   * Strip element content from autocapture-family events (`$autocapture`,
   * `$rageclick`, `$dead_click`): the SDK masks element text and attributes,
   * and the hook removes what it still records regardless (the link target of
   * the nearest anchor, `$external_click_url`) and keeps only each element's
   * tag, classes and sibling position. For an app whose lists render people's
   * names and link to their pages. Defaults to false.
   */
  maskAutocaptureContent?: boolean;
  /**
   * Page route templates (`/members/:id`). When set, every URL-bearing property
   * (`$current_url`, `$pathname`, `$referrer` and their `$initial_*`,
   * `$session_entry_*` and `$prev_pageview_*` forms, in properties, `$set` and
   * `$set_once`, plus web-vitals and heatmap URLs) is rewritten to its template
   * before sending. A path matching no template is destroyed, never passed
   * through. Absent leaves URLs as captured.
   */
  urlPageTemplates?: readonly string[];
}

/** See `PostHogConfig.entrySuppressedVisits`. */
export interface EntrySuppressedVisits {
  /** The page path whose visits may be suppressed. */
  targetPathPattern: RegExp;
  /** A visit is suppressed when the page before it matched one of these. */
  afterPathPatterns: readonly RegExp[];
  /**
   * A visit is also suppressed when this answers true for `history.state` on
   * arrival, which survives a reload of the same entry. A throw counts as true.
   */
  suppressWhenHistoryState?: (state: unknown) => boolean;
  /**
   * Called (outside the hook) when a visit starts suppressed, so the host can
   * mark the history entry and `suppressWhenHistoryState` recognises it after a
   * reload, when the page before it is no longer known. Must store no
   * identifier: the entry's own URL already names the page.
   */
  onSuppressedVisit?: (path: string) => void;
}

import { analyticsSubjectIdSchema } from '@galapagos/product-analytics';

/** Only the raw canonical member UUID crosses the analytics identity seam (ADR-069). */
export function isAnalyticsSubjectId(id: string): boolean {
  return analyticsSubjectIdSchema.safeParse(id).success;
}

export function resolveAnalyticsSelection(explicit?: string): AnalyticsSelection {
  const raw =
    explicit ?? (typeof process !== 'undefined' ? process.env?.ANALYTICS_PROVIDER : undefined);
  if (!raw || raw === 'noop') return 'noop';
  if (raw === 'posthog') return 'posthog';
  // ADR-053 Rule 4: an unrecognised selector fails fast rather than silently
  // dropping events by falling through to the default.
  throw new Error(
    `Unknown analytics provider "${raw}" — expected "noop" or "posthog" (ANALYTICS_PROVIDER).`,
  );
}
